import { z } from 'zod'
import type { FreeformProgressFrame } from '../../../utils/api-client/sse-schemas.ts'

// Hand-maintained mirror of analysis-service's `freeform/progress.py`, which the platform relays
// verbatim. Every event names a finding by `number`, its 1-based position in the extracted corpus.
// analysis-service serializes absent optionals as `null`, so they are `nullish` here.

const CodeExcerptSchema = z.object({ start_line: z.number().int(), lines: z.array(z.string()) })
export type CodeExcerpt = z.infer<typeof CodeExcerptSchema>

/** How a verified location was checked: the verifier read it, the report cited it, or the check could not run. */
export const LocationVerifiedNoteSchema = z.enum(['confirmed', 'cited', 'unchecked'])

const event = <TName extends string, TShape extends z.ZodRawShape>(name: TName, detail: TShape) =>
  z.object({
    seq: z.number().int(),
    ts: z.string(),
    message: z.string(),
    name: z.literal(name),
    detail: z.object(detail),
  })

const FreeformProgressEventSchema = z.discriminatedUnion('name', [
  event('file_read', { file: z.string(), kind: z.string() }),
  event('finding_extracted', {
    number: z.number().int(),
    total: z.number().int(),
    source_id: z.string().nullish(),
    title: z.string(),
    severity: z.string(),
    asset: z.string().nullish(),
  }),
  event('presearch_started', { findings: z.number().int(), clues: z.number().int(), repositories: z.number().int() }),
  event('finding_searching', {
    number: z.number().int(),
    clue: z.string(),
    hits: z.number().int(),
    repositories: z.array(z.string()).default([]),
    failed: z.boolean().default(false),
  }),
  event('copies_ignored', {
    number: z.number().int(),
    count: z.number().int(),
    paths: z.array(z.string()).default([]),
  }),
  event('batch_started', { batch: z.number().int(), numbers: z.array(z.number().int()) }),
  event('finding_matched', { number: z.number().int(), repository: z.string() }),
  event('location_found', {
    number: z.number().int(),
    repository: z.string(),
    path: z.string(),
    line: z.number().int().nullish(),
  }),
  event('location_verified', {
    number: z.number().int(),
    repository: z.string(),
    path: z.string(),
    line: z.number().int().nullish(),
    note: LocationVerifiedNoteSchema,
    reason: z.string(),
    excerpt: CodeExcerptSchema.nullish(),
  }),
  event('location_rejected', {
    number: z.number().int(),
    repository: z.string(),
    path: z.string(),
    line: z.number().int().nullish(),
    verdict: z.string(),
    reason: z.string(),
  }),
  event('finding_unassociated', { number: z.number().int(), reason: z.string(), rationale: z.string().default('') }),
  event('association_summary', {
    findings: z.number().int(),
    associated: z.number().int(),
    locations: z.number().int(),
    repositories: z.number().int(),
    unassociated: z.number().int(),
  }),
])
export type FreeformProgressEvent = z.infer<typeof FreeformProgressEventSchema>
export type FreeformProgressEventName = FreeformProgressEvent['name']

/** The frame as a typed event, or `undefined` when its name is unknown to this build or its detail malformed. */
export const parseFreeformProgress = (frame: FreeformProgressFrame): FreeformProgressEvent | undefined =>
  FreeformProgressEventSchema.safeParse(frame).data
